import Phaser from "phaser";

import { api } from "./api";
import { loadAvatarParts } from "./avatar/composer";
import { SocketClient } from "./net/SocketClient";
import { BootScene } from "./scenes/BootScene";
import { CityScene, type CityContext } from "./scenes/CityScene";
import { FlappyScene } from "./scenes/FlappyScene";
import { RunnerScene } from "./scenes/RunnerScene";
import { type ActiveSession, type Session, clearSession, loadSession, saveSession } from "./session";
import { showAuthOverlay } from "./ui/AuthOverlay";
import { Hud } from "./ui/Hud";
import { TouchControls } from "./ui/TouchControls";
import { type KickCode, PROFILE_RESET } from "./types";

const hud = new Hud();
const touch = new TouchControls();
let game: Phaser.Game | null = null;
let socket: SocketClient | null = null;

void start();

/** Fluxo principal: escolher a sessao, conectar ao mundo e subir o Phaser. */
async function start(
  notice?: { message: string; level: "ok" | "error" },
  trustedSession?: ActiveSession,
): Promise<void> {
  hud.hide();
  touch.setMode("hidden");
  teardownGame();

  const active = trustedSession ?? (await resolveSession(notice));

  let context: Omit<CityContext, "hud" | "touch" | "onLeave" | "onProfileUpdated" | "onKicked">;
  try {
    context = await connect(active);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Não foi possível entrar no mundo.";
    if (active.kind === "player") clearSession();
    await start({ message, level: "error" });
    return;
  }

  // As partes do avatar compoem os jogadores customizados; se falharem, os
  // avatares caem nos presets avatar_N e o jogo segue.
  await loadAvatarParts().catch((error) => console.warn(error));

  bootGame({
    ...context,
    hud,
    touch,
    onLeave: () => void leave(),
    onProfileUpdated: (session) => applyProfileUpdate(session),
    onKicked: (message, code) => void kicked(message, code),
  });
  if (pendingNotice) {
    hud.addSystem(pendingNotice, "warn");
    hud.showBanner(pendingNotice, "warn");
    pendingNotice = null;
  }
}

/** Aviso a mostrar no chat depois de reconectar (ver kicked). */
let pendingNotice: string | null = null;

/**
 * A moderacao derrubou a conexao. Nome, avatar ou cargo mudaram: entra de
 * novo (resolveSession recarrega o perfil) e mostra o motivo no chat. Nos
 * demais casos volta para a tela de entrada com o motivo.
 */
async function kicked(message: string, code: KickCode): Promise<void> {
  if (code === PROFILE_RESET) {
    pendingNotice = message;
    await start();
    return;
  }
  // Sessao encerrada, suspensa ou banida: o token salvo nao serve mais.
  if (code !== "kicked") clearSession();
  await start({ message, level: "error" });
}

/**
 * Avatar ou nome publico mudaram: guarda a sessao atualizada e reconecta ao
 * mundo (mesmo fluxo do boot) para o resto da cidade ver a troca. A sessao ja
 * acabou de ser confirmada pela propria chamada que atualizou o perfil, entao
 * pulamos a revalidacao via api.me() que resolveSession() faria de novo.
 */
function applyProfileUpdate(session: Session): void {
  saveSession(session);
  void start(undefined, { kind: "player", session });
}

/**
 * Reaproveita a sessao salva quando ela ainda e valida; caso contrario, mostra
 * a tela de entrada (login, cadastro ou visita anonima).
 */
async function resolveSession(notice?: { message: string; level: "ok" | "error" }): Promise<ActiveSession> {
  const hasActivationParam = new URLSearchParams(location.search).has("activation");
  const stored = notice || hasActivationParam ? null : loadSession();

  if (stored) {
    try {
      // O perfil salvo pode estar velho (ex.: avatar montado em outro aparelho).
      const { user } = await api.me(stored.token);
      const session = { ...stored, user };
      saveSession(session);
      return { kind: "player", session };
    } catch {
      clearSession();
    }
  }
  return showAuthOverlay(notice);
}

async function connect(active: ActiveSession) {
  const token = active.kind === "player" ? active.session.token : null;
  socket = new SocketClient(token);

  const welcome = await socket.connect();
  return { socket, welcome, session: active };
}

function bootGame(context: CityContext): void {
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game",
    backgroundColor: "#1b1d33",
    pixelArt: true,
    roundPixels: true,
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: "100%",
      height: "100%",
    },
    scene: [BootScene, CityScene, FlappyScene, RunnerScene],
  });

  game.registry.set("context", context);
}

async function leave(): Promise<void> {
  clearSession();
  await start({ message: "Você saiu da cidade. Até a próxima!", level: "ok" });
}

function teardownGame(): void {
  socket?.dispose();
  socket = null;
  game?.destroy(true);
  game = null;
}
